import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { selectAccountSlug } from '@/redux/session/selectors';
import { AIAgent } from '@/shared/types/brain';

import { useStartingNode } from '../../hooks/useStartingNode';

const NODE_NAME_CLASSES = 'min-w-0 truncate text-sm leading-5 font-normal';

interface StartingNodeCellProps {
  agent: AIAgent;
}

export const StartingNodeCell = ({ agent }: StartingNodeCellProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { data: node, isPending } = useStartingNode(agent.brain_id, agent.starting_node_id);
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [node?.nodeName]);

  if (!agent.starting_node_id) {
    return <span className="text-foreground">-</span>;
  }

  if (isPending) {
    return <Skeleton className="h-4 w-24" />;
  }

  if (node === undefined) {
    return <span className="text-foreground">-</span>;
  }

  if (node === null) {
    return <span className="text-muted-foreground">{t('brains.unknown_node')}</span>;
  }

  const dialogHref = `/${slug}/ai-agents/${agent.brain_id}/workflows/dialogs/${node.dialogId}`;

  return (
    <InfoTooltip
      content={isTruncated ? node.nodeName : t('brains.go_to_dialog')}
      size={isTruncated ? 'auto' : 'default'}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(dialogHref, '_blank', 'noopener,noreferrer');
        }}
        className="group/starting-node flex max-w-full min-w-0 cursor-pointer items-center gap-1 text-left text-foreground hover:text-primary"
      >
        <span ref={ref} className={NODE_NAME_CLASSES}>
          {node.nodeName}
        </span>
        <ExternalLinkIcon className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover/starting-node:opacity-100" />
      </button>
    </InfoTooltip>
  );
};
